import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '../../app/store'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import nots from '../../assets/images/nots-color.svg'
import codedescond from '../../assets/images/codedescond.svg'
import { clearDiscount } from '../../features/discount/discountSlice'
import { setNotes } from '../../features/cart/cartSlice'
import { useRemoveCouponMutation } from '../../features/cart/cartAPI'
import { loadCartItems, selectCartInfo } from '../../features/cart/cartItemsSlice'
import { selectIsCsFlowComplete } from '../../features/csFlow/csFlowSlice'
import { refreshCsCartItems } from '../../features/csFlow/csCartSlice'
import { isCustomerServiceUser } from '../../utils/apiUtils'

interface DiscountNotesSectionProps {
  onDiscountClick?: () => void
  onNotesClick?: () => void
  currentNote?: string
}

const DiscountNotesSection: React.FC<DiscountNotesSectionProps> = ({
  onDiscountClick,
  onNotesClick,
  currentNote
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const { hasDiscount, discountCode } = useSelector((state: RootState) => state.discount)
  const { hasCustomerData, customerId } = useSelector((state: RootState) => state.customer)
  const csCustomerId = useSelector((state: RootState) => state.csFlow.customerId)
  const cartInfo = useSelector(selectCartInfo)
  const { settings } = useSelector((state: RootState) => state.settings)
  const notesFromRedux = useSelector((state: RootState) => state.cart.notes)
  const isRTL = settings.language === 'ar'
  const [removeCoupon] = useRemoveCouponMutation()

  // Use Redux store as source of truth, fallback to prop for backward compatibility
  const note = notesFromRedux || currentNote || ''
  const hasNote = note && note.trim().length > 0

  // Truncate note text to 7 characters
  const truncateNote = (text: string | undefined): string => {
    if (!text) return ''
    if (text.length <= 7) return text
    return text.substring(0, 7) + '...'
  }

  // Check if user is CS and flow is complete
  const isCsFlowComplete = useSelector(selectIsCsFlowComplete)

  // Check if current user is Customer Service
  const isCustomerService = React.useMemo(() => isCustomerServiceUser(), [])

  // Disable if CS user and flow is not complete
  const isDisabled = isCustomerService && !isCsFlowComplete

  // Get userId helper function
  const getUserId = (): number => {
    try {
      if (hasCustomerData && customerId) {
        return customerId
      }

      // Try localStorage
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.user?.id) {
          return Number(employeeData.user.id)
        }
        if (employeeData.user_id) {
          return Number(employeeData.user_id)
        }
      }
    } catch (e) {
      // Ignore
    }
    return 7 // Default fallback
  }

  const handleClearNote = async (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent opening the modal when clicking X
    try {
      dispatch(setNotes(''))
    } catch (error) {
    }
  }

  const handleClearDiscount = async (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent opening the modal when clicking X
    // Clear before reloading: cart loaders fall back to the Redux discount code
    dispatch(clearDiscount())
    try {
      if (isCustomerService && csCustomerId > 0) {
        // Refresh cart with empty coupon code to remove discount
        await dispatch(refreshCsCartItems({
          customerId: csCustomerId,
          forceRefresh: true,
          couponCode: '' // Empty coupon code to remove discount
        }) as any)
      } else {
        // Cashier Flow: Use regular cart
        const userId = getUserId()
        const tableId = cartInfo?.table_id ? Number(cartInfo.table_id) : undefined

        // Remove coupon using cart API
        await removeCoupon({
          userId,
          tableId,
        }).unwrap()

        // Reload cart items to reflect the updated prices
        await dispatch(loadCartItems({ forceRefresh: true, tableId }) as any)
      }
    } catch (error: any) {
      // Try to reload cart anyway
      try {
        if (isCustomerService && csCustomerId > 0) {
          await dispatch(refreshCsCartItems({
            customerId: csCustomerId,
            forceRefresh: true,
            couponCode: ''
          }) as any)
        } else {
          const tableId = cartInfo?.table_id ? Number(cartInfo.table_id) : undefined
          await dispatch(loadCartItems({ forceRefresh: true, tableId }) as any)
        }
      } catch (e) {
        // Ignore
      }
    }
  }

  return (
    <div className="flex gap-2 shrink-0 relative">
      {/* Gray overlay when disabled */}
      {isDisabled && (
        <div className="absolute inset-0 bg-white bg-opacity-50 z-10 rounded-xl" style={{ pointerEvents: 'auto' }} />
      )}

      <div
        onClick={isDisabled ? undefined : onNotesClick}
        className={`flex-1 h-14 bg-white rounded-xl px-4 flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
      >
        <div className="w-5 h-5 text-flutter-button">
          {/* note icon */}
          <img src={nots} alt="commit" className='h-[22px] w-[22px] text-black  items-center ' />
        </div>
        <div className="flex-1 text-sm font-medium truncate">
          {hasNote ? truncateNote(note) : t('common.notes')}
        </div>
        {hasNote ? (
          <button
            onClick={handleClearNote}
            disabled={isDisabled}
            className="h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0"
            aria-label={t('common.remove') || 'Remove note'}
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        ) : (
          isRTL ? (
            <ChevronLeft className="w-5 h-5 text-[#525866] flex-shrink-0" />
          ) : (
            <ChevronRight className="w-5 h-5 text-[#525866] flex-shrink-0" />
          )
        )}
      </div>
      <div
        onClick={isDisabled ? undefined : onDiscountClick}
        className={`flex-1 h-14 bg-white rounded-xl px-4 flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
      >
        <img src={codedescond} alt="code" className='h-[22px] w-[22px] text-black items-center ' />
        <div className="flex-1 text-sm font-medium truncate">
          {hasDiscount && discountCode ? discountCode : t('discount.discount_code')}
        </div>
        {hasDiscount && discountCode ? (
          <button
            onClick={handleClearDiscount}
            disabled={isDisabled}
            className="h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0"
            aria-label={t('common.remove') || 'Remove discount'}
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        ) : (
          isRTL ? (
            <ChevronLeft className="w-5 h-5 text-[#525866] flex-shrink-0" />
          ) : (
            <ChevronRight className="w-5 h-5 text-[#525866] flex-shrink-0" />
          )
        )}
      </div>
    </div>
  )
}

export default DiscountNotesSection

